
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularIndeterminate, FormButton } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import { FORM_ERROR_MESSAGES } from "consts/errorMessages";
import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { paisGet, SFHotelEmpleadoIns, tipoDeDocumentoGet, usuarioBasicosGet } from "services/services-mia";

const sexos = [
    { sexoCod: "", sexoNombre: "Seleccionar" },
    { sexoCod: "M", sexoNombre: "Masculino" },
    { sexoCod: "F", sexoNombre: "Femenino" },
    { sexoCod: "X", sexoNombre: "No Binario" }
];

const emailRegex = /^(([^<>()[\]\\.,;:#\s@"]+(\.[^<>()[\]\\.,;:#\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export default function InsertModal({ updateList, roles, closeModal, hotelId }) {
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const [inRequest, setInRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tiposDeDocumento, setTiposDeDocumento] = useState([]);
    const [placeholderDNI, setPlaceholderDNI] = useState('');
    const [regexDNI, setRegexDNI] = useState(/./);

    const formikRef = useRef();

    const functions = {
        setSnackbarInfo,
        setInRequest,
        setIsOpen: closeModal,
        updateList,
    };

    const [step, setStep] = useState({
        basico: true,
        contacto: false,
    });

    const getTiposDeDocumento = useCallback(async (paisId) => {
        if (!paisId) return;
        setIsLoading(true);
        const { result, status } = await tipoDeDocumentoGet(paisId, credentials);
        if (status.code !== 1) {
            setIsLoading(false);
            return;
        }
        setTiposDeDocumento(result);
        if (result.length > 0 && formikRef.current) {
            const first = result[0];
            formikRef.current.setFieldValue("tipoDeDocumento", first.tipoDeDocumentoCod);
            setPlaceholderDNI(first.tipoDeDocumentoPlaceholder);
            setRegexDNI(first.tipoDeDocumentoRegExp || /./);
        }
        setIsLoading(false);
    }, [credentials]);

    // Carga inicial: obtener tipos de documento
    useEffect(() => {
        if (!credentials || Object.keys(credentials).length === 0) return;
        (async () => {
            const { result: paises, status } = await paisGet(credentials);
            if (status.code === 1 && paises?.[0]) {
                await getTiposDeDocumento(paises[0].paisId);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credentials]);

    const handleChangeTipoDeDocumento = (event) => {
        const tipoDeDocumentoCod = event.target.value;
        const tipoDeDocumento = tiposDeDocumento.find(td => td.tipoDeDocumentoCod === tipoDeDocumentoCod);
        if (tipoDeDocumento && formikRef.current) {
            formikRef.current.setFieldValue("tipoDeDocumento", tipoDeDocumento.tipoDeDocumentoCod);
            setPlaceholderDNI(tipoDeDocumento.tipoDeDocumentoPlaceholder);
            setRegexDNI(tipoDeDocumento.tipoDeDocumentoRegExp || /./);
        }
    };

    const handleConnection = async (values, resetForm) => {
        setInRequest(true);
        try {
            const formattedDNI = values.dni.replace(/\D/g, "").slice(-8);
            const tipoDeDocumentoId = tiposDeDocumento.find(td => td.tipoDeDocumentoCod === values.tipoDeDocumento)?.tipoDeDocumentoId;

            if (!tipoDeDocumentoId) {
                setSnackbarInfo({
                    message: "Tipo de documento no válido",
                    severity: "error",
                    open: true,
                });
                setInRequest(false);
                return;
            }

            const response = await usuarioBasicosGet({
                tipoDeDocumentoId,
                usuarioNroDeDocumento: formattedDNI,
                usuarioSexo: values.sexo,
                usuarioFechaDeNacimiento: values.fechaNacimiento,
                tipoDeDocumentoCod: values.tipoDeDocumento,
            }, credentials);

            // Validar que la respuesta tenga la estructura esperada
            if (!response || !response.status) {
                setSnackbarInfo({
                    message: "Error inesperado en la respuesta del servidor",
                    severity: "error",
                    open: true,
                });
                setInRequest(false);
                return;
            }

            // Si hay error en la respuesta
            if (response.status.code === 0) {
                setSnackbarInfo({
                    message: response.status.errmsg || "Error al buscar usuario",
                    severity: "error",
                    open: true,
                });
                setInRequest(false);
                return;
            }

            if (response.result.length === 0) {
                setStep({ basico: false, contacto: true });
                formikRef.current?.setFieldTouched("nombre", false);
                formikRef.current?.setFieldTouched("apellido", false);
                formikRef.current?.setFieldTouched("email", false);
                setInRequest(false);
                return;
            }

            if (response.result.length === 1) {
                const responseIns = await SFHotelEmpleadoIns({
                    SFHotelId: hotelId,
                    tipoDeDocumentoCod: values.tipoDeDocumento,
                    usuarioNroDeDocumentoX: formattedDNI,
                    usuarioSexo: values.sexo,
                    usuarioFechaDeNacimiento: values.fechaNacimiento,
                    usuarioApellidos: response.result[0].usuarioApellidos,
                    usuarioNombres: response.result[0].usuarioNombres,
                    usuarioMail: response.result[0].usuarioMail,
                    rolCod: values.rol,
                }, credentials);


                // Validar respuesta de inserción
                if (!responseIns || !responseIns.status) {
                    setSnackbarInfo({
                        message: "Error inesperado al guardar",
                        severity: "error",
                        open: true,
                    });
                    setInRequest(false);
                    return;
                }

                handleResponse(
                    responseIns,
                    "Empleado agregado exitosamente",
                    functions
                );

                if (responseIns.status.code === 1) {
                    resetForm();
                }
                setInRequest(false);
                return;
            }

            if (response.result.length > 1) {
                setSnackbarInfo({
                    message: "Hay más de un empleado con esos datos. Verifique documento, sexo y fecha de nacimiento o contacte soporte.",
                    severity: "warning",
                    open: true,
                });
            }
        } catch (error) {
            console.error("Error en handleConnection:", error);
            setSnackbarInfo({
                message: error.message || "Error inesperado",
                severity: "error",
                open: true,
            });
        } finally {
            setInRequest(false);
        }
    };

    const handleConnectionContact = async (values, resetForm) => {
        setInRequest(true);
        try {
            const responseIns = await SFHotelEmpleadoIns({
                SFHotelId: hotelId,
                tipoDeDocumentoCod: values.tipoDeDocumento,
                usuarioNroDeDocumentoX: values.dni,
                usuarioSexo: values.sexo,
                usuarioFechaDeNacimiento: values.fechaNacimiento,
                usuarioApellidos: values.apellido,
                usuarioNombres: values.nombre,
                usuarioMail: values.email,
                rolCod: values.rol,
            }, credentials);

            // Validar que la respuesta tenga la estructura esperada
            if (!responseIns || !responseIns.status) {
                setSnackbarInfo({
                    message: "Error inesperado en la respuesta del servidor",
                    severity: "error",
                    open: true,
                });
                setInRequest(false);
                return;
            }

            // Si hay error en la respuesta
            if (responseIns.status.code === 0) {
                setSnackbarInfo({
                    message: responseIns.status.errmsg || "Error al guardar",
                    severity: "error",
                    open: true,
                });
                setInRequest(false);
                return;
            }

            // Si es exitoso
            handleResponse(
                responseIns,
                "Empleado agregado exitosamente",
                functions
            );

            if (responseIns.status.code === 1) {
                resetForm();
            }
        } catch (error) {
            console.error("Error en handleConnectionContact:", error);
            setSnackbarInfo({
                message: error.message || "Error inesperado",
                severity: "error",
                open: true,
            });
        } finally {
            setInRequest(false);
        }
    };

    const validationSchema = yup.object({
        dni: yup.string(FORM_ERROR_MESSAGES.text)
            .required(FORM_ERROR_MESSAGES.required)
            .matches(regexDNI, { message: "Número de documento inválido" }),
        nombre: step.contacto
            ? yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required)
            : yup.string(FORM_ERROR_MESSAGES.text).nullable(),
        email: step.contacto
            ? yup.string(FORM_ERROR_MESSAGES.text)
                .required(FORM_ERROR_MESSAGES.required)
                .matches(emailRegex, { message: "Email inválido" })
            : yup.string(FORM_ERROR_MESSAGES.text).nullable(),
        sexo: step.basico
            ? yup.string(FORM_ERROR_MESSAGES.text)
                .required(FORM_ERROR_MESSAGES.required)
                .oneOf(sexos.map((s) => s.sexoCod))
            : yup.string(FORM_ERROR_MESSAGES.text).nullable(),
        rol: step.basico
            ? yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required)
            : yup.string(FORM_ERROR_MESSAGES.text).nullable(),
        fechaNacimiento: step.basico
            ? yup.date()
                .required(FORM_ERROR_MESSAGES.required)
                .max(new Date(new Date().setFullYear(new Date().getFullYear() - 18)), "Debe ser mayor de 18 años")
                .min(new Date(new Date().setFullYear(new Date().getFullYear() - 100)), "Fecha inválida (máximo 100 años)")
            : yup.date().nullable(),
    });

    const selectData = {
        rol: { label: "Rol" }
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                dni: "",
                email: "",
                sexo: "",
                rol: "",
                nombre: "",
                apellido: "",
                fechaNacimiento: "",
                tipoDeDocumento: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
                try {
                    if (step.basico) {
                        await handleConnection(values, resetForm);
                    } else if (step.contacto) {
                        await handleConnectionContact(values, resetForm);
                    }
                } catch (e) {
                    setSnackbarInfo({
                        message: e.errmsg || "Error al guardar",
                        severity: "error",
                        open: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Form className={styles.inputs} style={{ padding: '1rem' }} noValidate>
                <div className={styles["column-sm"]}>
                    {step.basico && (
                        <>
                            <FormikSelectInput
                                fullWidth={true}
                                name="tipoDeDocumento"
                                labelText="Tipo de documento"
                                onChange={handleChangeTipoDeDocumento}
                            >
                                {tiposDeDocumento.map(({ tipoDeDocumentoCod, tipoDeDocumentoNombre }) => (
                                    <option key={tipoDeDocumentoCod} value={tipoDeDocumentoCod}>
                                        {tipoDeDocumentoNombre}
                                    </option>
                                ))}
                            </FormikSelectInput>
                            <FormikTextInput
                                fullWidth={true}
                                name="dni"
                                placeholder={placeholderDNI}
                                labelText="Número de Documento"
                                inputProps={{ maxLength: 10 }}
                            />
                            <FormikSelectInput
                                fullWidth={true}
                                name="rol"
                                labelText="Rol"
                            >
                                <option value="">Seleccione un rol</option>
                                {(roles ?? []).map(({ rolCod, rolNombre }) => (
                                    <option key={rolCod} value={rolCod}>
                                        {rolNombre}
                                    </option>
                                ))}
                            </FormikSelectInput>
                            <FormikSelectInput fullWidth={true} name="sexo" labelText="Género">
                                {sexos.map(({ sexoCod, sexoNombre }) => (
                                    <option key={sexoCod} value={sexoCod}>
                                        {sexoNombre}
                                    </option>
                                ))}
                            </FormikSelectInput>
                            <FormikTextInput
                                fullWidth={true}
                                name="fechaNacimiento"
                                labelText="Fecha de nacimiento"
                                type="date"
                            />
                        </>
                    )}

                    {step.contacto && (
                        <>
                            <FormikTextInput
                                fullWidth={true}
                                name="nombre"
                                labelText="Nombre"
                                placeholder="Juan"
                            />
                            <FormikTextInput
                                fullWidth={true}
                                name="apellido"
                                labelText="Apellido"
                                placeholder="Galvan"
                            />
                            <FormikTextInput
                                fullWidth={true}
                                name="email"
                                labelText="Mail"
                                placeholder="ejemplo@gmail.com"
                            />
                        </>
                    )}

                    <FormButton
                        inRequest={inRequest || isLoading}
                        newFormData={{ button: "Guardar cambios" }}
                        onCloseModal={closeModal}
                    />

                    {(inRequest || isLoading) && <CircularIndeterminate />}
                </div>
            </Form>
        </Formik>
    );
}